(() => {
  'use strict';

  const AUTO_GIFT_PROPERTY = '_aura_auto_gift';
  const STATUS_SELECTOR = '[data-zepp-aura-status], [data-zepp-aura-global-status]';

  class ZeppAuraAutoGiftController {
    constructor(configElement) {
      this.configElement = configElement;
      this.enabled = configElement.dataset.enabled === 'true';
      this.debug = configElement.dataset.debug === 'true';
      this.giftVariantId = Number.parseInt(configElement.dataset.giftVariantId || '', 10);
      this.eligibleVariantIds = new Set(
        (configElement.dataset.eligibleVariantIds || '')
          .split(',')
          .map((value) => Number.parseInt(value.trim(), 10))
          .filter(Number.isFinite)
      );
      this.eligibleVariantIds.delete(this.giftVariantId);
      this.inFlight = false;
      this.pending = false;
      this.timer = null;
      this.lastFailedToken = '';
      this.cooldownUntil = 0;

      this.onCartEvent = this.onCartEvent.bind(this);
      this.onPageShow = this.onPageShow.bind(this);

      document.addEventListener('variant:added', this.onCartEvent);
      document.documentElement.addEventListener('cart:updated', this.onCartEvent);
      document.documentElement.addEventListener('cart:refresh', this.onCartEvent);
      window.addEventListener('pageshow', this.onPageShow);

      if (!this.enabled) {
        this.setStatus('inactive', 'Zepp Aura特典の自動追加はPreview設定でOFFです。');
        return;
      }

      if (!Number.isFinite(this.giftVariantId) || this.eligibleVariantIds.size === 0) {
        this.setStatus('error', '自動追加の対象商品または特典商品が未設定です。');
        this.log('Missing gift variant or eligible variants.');
        return;
      }

      this.scheduleSync('initial');
    }

    destroy() {
      window.clearTimeout(this.timer);
      document.removeEventListener('variant:added', this.onCartEvent);
      document.documentElement.removeEventListener('cart:updated', this.onCartEvent);
      document.documentElement.removeEventListener('cart:refresh', this.onCartEvent);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    log(...args) {
      if (this.debug) console.info('[Zepp Aura auto gift]', ...args);
    }

    onPageShow() {
      this.scheduleSync('pageshow');
    }

    onCartEvent(event) {
      const cart = event.detail && event.detail.cart ? event.detail.cart : null;
      this.scheduleSync(event.type, cart);
    }

    scheduleSync(reason, cart = null) {
      if (!this.enabled || !Number.isFinite(this.giftVariantId) || this.eligibleVariantIds.size === 0) return;

      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.sync(reason, cart), 120);
    }

    async sync(reason, suppliedCart = null) {
      if (this.inFlight) {
        this.pending = true;
        return;
      }

      if (Date.now() < this.cooldownUntil) return;

      this.inFlight = true;
      this.pending = false;
      this.log('Sync start:', reason);

      try {
        const cart = suppliedCart && Array.isArray(suppliedCart.items) ? suppliedCart : await this.fetchCart();
        const token = this.cartToken(cart);
        const eligibleItems = cart.items.filter((item) => this.eligibleVariantIds.has(Number(item.variant_id || item.id)));
        const autoGiftItems = cart.items.filter((item) => (
          Number(item.variant_id || item.id) === this.giftVariantId &&
          item.properties &&
          String(item.properties[AUTO_GIFT_PROPERTY]) === 'true'
        ));

        if (eligibleItems.length === 0) {
          if (autoGiftItems.length > 0) {
            await this.applyChanges(autoGiftItems.map((item) => ({ id: item.key, quantity: 0 })));
            await this.refreshThemeCart('removed');
          } else {
            this.setStatus('idle', '対象製品をカートに追加すると、Zepp Aura特典を1点だけ自動で追加します。');
          }
          this.lastFailedToken = '';
          return;
        }

        if (autoGiftItems.length === 0) {
          const failureToken = `${token}:add`;
          if (failureToken === this.lastFailedToken) return;

          this.setStatus('working', 'Zepp Aura特典をカートへ追加しています…');
          try {
            await this.addGift();
            await this.refreshThemeCart('added');
            this.lastFailedToken = '';
          } catch (error) {
            this.lastFailedToken = failureToken;
            throw error;
          }
          return;
        }

        const changes = [];
        autoGiftItems.forEach((item, index) => {
          if (index > 0) changes.push({ id: item.key, quantity: 0 });
          else if (item.quantity !== 1) changes.push({ id: item.key, quantity: 1 });
        });

        if (changes.length > 0) {
          const failureToken = `${token}:normalize`;
          if (failureToken === this.lastFailedToken) return;
          try {
            await this.applyChanges(changes);
            await this.refreshThemeCart('normalized');
            this.lastFailedToken = '';
          } catch (error) {
            this.lastFailedToken = failureToken;
            throw error;
          }
          return;
        }

        this.reportAppliedState(autoGiftItems[0]);
        this.lastFailedToken = '';
      } catch (error) {
        this.cooldownUntil = Date.now() + 3000;
        this.setStatus('error', 'Zepp Aura特典を追加できませんでした。カート内容と特典在庫をご確認ください。');
        this.log('Sync failed:', error);
      } finally {
        this.inFlight = false;
        if (this.pending) this.scheduleSync('pending');
      }
    }

    cartToken(cart) {
      return cart.items
        .map((item) => `${item.key}:${item.quantity}`)
        .sort()
        .join('|');
    }

    async fetchCart() {
      const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
      return response.json();
    }

    async addGift() {
      const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: this.giftVariantId,
            quantity: 1,
            properties: { [AUTO_GIFT_PROPERTY]: 'true' }
          }]
        })
      });

      if (!response.ok) throw new Error(await this.responseError(response, 'Gift add failed'));
    }

    async applyChanges(changes) {
      for (const change of changes) {
        const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(change)
        });

        if (!response.ok) throw new Error(await this.responseError(response, 'Gift change failed'));
      }
    }

    async responseError(response, fallback) {
      try {
        const body = await response.json();
        return body.description || body.message || `${fallback}: ${response.status}`;
      } catch (_error) {
        return `${fallback}: ${response.status}`;
      }
    }

    async refreshThemeCart(action) {
      const cart = await this.fetchCart();
      const autoGift = cart.items.find((item) => (
        Number(item.variant_id || item.id) === this.giftVariantId &&
        item.properties &&
        String(item.properties[AUTO_GIFT_PROPERTY]) === 'true'
      ));

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart }
      }));

      if (action === 'removed') {
        this.setStatus('idle', '対象製品がないため、自動追加されたZepp Aura特典を削除しました。');
      } else if (autoGift) {
        this.reportAppliedState(autoGift);
      }
    }

    reportAppliedState(autoGift) {
      const linePrice = Number(autoGift.final_line_price);
      if (Number.isFinite(linePrice) && linePrice === 0) {
        this.setStatus('applied', 'Zepp Aura特典を1点追加し、割引適用を確認しました。');
      } else {
        this.setStatus('warning', 'Zepp Aura特典を1点追加しました。割引適用はカートで必ずご確認ください。');
      }
    }

    setStatus(state, message) {
      document.querySelectorAll(STATUS_SELECTOR).forEach((element) => {
        element.dataset.state = state;
        element.textContent = message;
      });
      document.dispatchEvent(new CustomEvent('zepp-aura:status', {
        detail: { state, message }
      }));
    }
  }

  function initialize() {
    const configElement = document.querySelector('[data-zepp-aura-auto-gift]');
    if (!configElement) return;

    if (window.ZeppAuraAutoGift && typeof window.ZeppAuraAutoGift.destroy === 'function') {
      window.ZeppAuraAutoGift.destroy();
    }

    window.ZeppAuraAutoGift = new ZeppAuraAutoGiftController(configElement);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-zepp-aura-auto-gift]')) initialize();
  });
})();
